import { Box, Flex, Tooltip } from '@chakra-ui/react';
import IconCircleInfo from 'central-icons/IconCircleInfo';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { components, type OptionProps } from 'react-select';

import styles from './option.module.css';
import type { Option } from './SelectionListSelect';
import { copyTextToClipboard, getDisplayIdFromNodeId } from './utils';

type CustomOptionProps<IsMulti extends boolean = true> = OptionProps<
  Option,
  IsMulti
> & {
  showAdminId?: boolean;
};

export const CustomOption = <IsMulti extends boolean = true>(
  props: CustomOptionProps<IsMulti>
) => {
  const fullAdminIdText = props.data.value;
  const displayAdminIdText = getDisplayIdFromNodeId(fullAdminIdText);
  const showAdminId =
    props.showAdminId &&
    displayAdminIdText.length > 0 &&
    fullAdminIdText.length > 0;
  const [isAdminIdCopied, setIsAdminIdCopied] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyAdminId = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (!fullAdminIdText) return;
    const copied = await copyTextToClipboard(fullAdminIdText);
    if (!copied) return;
    setIsAdminIdCopied(true);
    if (copyTimeoutRef.current !== null) {
      window.clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = window.setTimeout(() => {
      setIsAdminIdCopied(false);
    }, 1200);
  };
  return (
    <components.Option {...props}>
      <Flex
        data-testid="selection-tree-data-node"
        data-option-value={props.data.value}
        data-option-label={props.data.label}
        alignItems="center"
        width="100%"
        overflow="hidden"
      >
        <Flex alignItems="center" flex="1" minWidth={0} overflow="hidden">
          <Flex flexShrink={0} alignItems="center">
            <input
              className={styles.checkbox}
              type="checkbox"
              checked={props.isSelected}
              disabled={props.isDisabled}
              onChange={() => null}
            />
            <span className={styles.checkmark}></span>
          </Flex>
          <label className={styles.label}>{props.label}</label>
          {props.data.info && (
            <Tooltip
              label={props.data.info}
              placement="right"
              hasArrow
              bg="text.500"
              padding="12px"
              fontSize="12px"
              maxWidth="330px"
              borderRadius="4px"
            >
              <Box as="span" marginLeft={0.5}>
                <IconCircleInfo size={14} color="#96a0b5" cursor="pointer" />
              </Box>
            </Tooltip>
          )}
        </Flex>
        {showAdminId && (
          <button
            type="button"
            className={classNames(
              styles.adminId,
              isAdminIdCopied && styles.adminIdCopied
            )}
            onClick={handleCopyAdminId}
            title={`Copy id: ${fullAdminIdText}`}
            data-copied={isAdminIdCopied ? 'true' : 'false'}
          >
            {isAdminIdCopied ? 'copied' : `id: ${displayAdminIdText}`}
          </button>
        )}
      </Flex>
    </components.Option>
  );
};
